import React, { useState, useEffect, useRef } from 'react';
import { 
    FaBullhorn, 
    FaUsers, 
    FaFilter, 
    FaUserFriends, 
    FaGraduationCap, 
    FaHospital, 
    FaStar,
    FaBuilding,
    FaExclamationTriangle,
    FaInfoCircle,
    FaClock,
    FaPaperPlane,
    FaCheckCircle,
    FaTimesCircle,
    FaEye
} from 'react-icons/fa';
import axiosInstance from '../utils/axiosConfig.js';
import { normalizeUsers } from '../utils/userUtils';
import LoadingSpinner from './LoadingSpinner';
import { getActionErrorMessage } from '../utils/errorHandler';
import { useToast } from '../utils/toastContext';
import './MakeAnnouncement.css';

const MakeAnnouncement = () => {
    const { showSuccess, showError } = useToast();
    const [announcement, setAnnouncement] = useState({
        title: '',
        message: '',
        target_type: 'filters',
        target_user_ids: [],
        target_filters: {
            curriculums: [],
            account_types: [],
            ranks: [],
            specialities: [],
            institutions: []
        },
        priority: 'medium',
        expires_at: ''
    });

    const [filterOptions, setFilterOptions] = useState({
        curriculums: [],
        account_types: [],
        ranks: [],
        specialities: [],
        institutions: []
    });

    const [loading, setLoading] = useState(false);
    const [previewMode, setPreviewMode] = useState(false);
    
    // For announcement status polling
    const [announcementStatus, setAnnouncementStatus] = useState(null);
    const [pollingInterval, setPollingInterval] = useState(null);
    const hasShownSuccessRef = useRef(false); // Use ref to prevent multiple success messages across re-renders
    
    // For explicit user selection
    const [students, setStudents] = useState([]);
    const [preceptors, setPreceptors] = useState([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    useEffect(() => {
        fetchFilterOptions();
        fetchUsers(); // Fetch users for explicit selection
    }, []);

    // Cleanup polling interval on unmount
    useEffect(() => {
        return () => {
            if (pollingInterval) {
                clearInterval(pollingInterval);
            }
        };
    }, [pollingInterval]);

    const fetchFilterOptions = async () => {
        try {
            const response = await axiosInstance.get('/announcements/filters/options');
            setFilterOptions(response.data);
        } catch (err) {
            console.error('Error fetching filter options:', err);
            showError('Failed to load filter options');
        }
    };

    const fetchUsers = async () => {
        try {
            setLoadingUsers(true);
            const [studentsRes, preceptorsRes] = await Promise.all([
                axiosInstance.get('/crud/get_all_students_basic_info/'),
                axiosInstance.get('/crud/get_all_preceptors_basic_info/')
            ]);
            setStudents(normalizeUsers(studentsRes.data || []));
            setPreceptors(normalizeUsers(preceptorsRes.data || []));
        } catch (err) {
            console.error('Error fetching users:', err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const pollAnnouncementStatus = async (announcementId) => {
        try {
            const response = await axiosInstance.get(`/announcements/${announcementId}/status`);
            const statusData = response.data;
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('📊 Polling status:', statusData);
            
            setAnnouncementStatus(statusData);
            
            // Stop polling if processing is complete
            if (!statusData.is_processing) {
                if (pollingInterval) {
                    clearInterval(pollingInterval);
                    setPollingInterval(null);
                }
                
                setLoading(false);
                
                // Show final success message ONLY ONCE using ref
                if (!hasShownSuccessRef.current) {
                    hasShownSuccessRef.current = true;
                    
                    const successRate = statusData.success_rate || 0;
                    const sent = statusData.notifications_sent || 0;
                    const failed = statusData.notifications_failed || 0;
                    const total = sent + failed;
                    
                    if (successRate >= 99) {
                        showSuccess(`🎉 Successfully sent ${sent}/${total} notifications (${successRate.toFixed(1)}% success rate)!`);
                    } else {
                        showSuccess(`✅ Sent ${sent}/${total} notifications (${successRate.toFixed(1)}% success rate). ${failed} failed.`);
                    }
                    
                    // Reset form after delay
                    setTimeout(() => {
                        setAnnouncement({
                            title: '',
                            message: '',
                            target_type: 'filters',
                            target_user_ids: [],
                            target_filters: {
                                curriculums: [],
                                account_types: [],
                                ranks: [],
                                specialities: [],
                                institutions: []
                            },
                            priority: 'medium',
                            expires_at: ''
                        });
                        setAnnouncementStatus(null);
                        hasShownSuccessRef.current = false; // Reset for next submission
                    }, 3000);
                }
            }
        } catch (err) {
            console.error('❌ Error polling status:', err);
            // Don't set error here, let the process continue
        }
    };



    const handleInputChange = (field, value) => {
        setAnnouncement(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleFilterChange = (filterType, value) => {
        setAnnouncement(prev => ({
            ...prev,
            target_filters: {
                ...prev.target_filters,
                [filterType]: prev.target_filters[filterType].includes(value)
                    ? prev.target_filters[filterType].filter(item => item !== value)
                    : [...prev.target_filters[filterType], value]
            }
        }));
    };



    const getPriorityIcon = (priority) => {
        switch (priority) {
            case 'high': return <FaExclamationTriangle className="priority-icon high" />;
            case 'medium': return <FaInfoCircle className="priority-icon medium" />;
            case 'low': return <FaCheckCircle className="priority-icon low" />;
            default: return <FaInfoCircle className="priority-icon medium" />;
        }
    };

    const getPriorityLabel = (priority) => {
        switch (priority) {
            case 'high': return 'High Priority';
            case 'medium': return 'Medium Priority';
            case 'low': return 'Low Priority';
            default: return 'Medium Priority';
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setAnnouncementStatus(null);
        hasShownSuccessRef.current = false; // Reset success flag for new submission

        try {
            // Validate that at least one filter is selected or user IDs are provided (mutually exclusive)
            if (announcement.target_type === 'filters') {
                const hasFilters = Object.values(announcement.target_filters).some(arr => arr.length > 0);
                if (!hasFilters) {
                    showError('Please select at least one filter option to target users.');
                    setLoading(false);
                    return;
                }
                // Clear explicit user IDs when using filters
                announcement.target_user_ids = [];
            } else if (announcement.target_type === 'explicit') {
                if (announcement.target_user_ids.length === 0) {
                    showError('Please select at least one user (student or preceptor).');
                    setLoading(false);
                    return;
                }
                // Clear filters when using explicit selection
                announcement.target_filters = {
                    curriculums: [],
                    account_types: [],
                    ranks: [],
                    specialities: [],
                    institutions: []
                };
            }

            const payload = {
                ...announcement,
                expires_at: announcement.expires_at || null
            };

            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('📤 Creating announcement:', payload);

            // Create announcement (instant response with announcement_id)
            const response = await axiosInstance.post('/announcements/create', payload);
            
            if (import.meta.env.VITE_DEBUG_MODE === 'true') console.log('✅ Announcement created:', response.data);
            
            const { announcement_id, target_count } = response.data;
            
            // Start showing initial progress
            setAnnouncementStatus({
                status: 'processing',
                notifications_sent: 0,
                notifications_failed: 0,
                target_count: target_count,
                is_processing: true
            });
            
            // Start polling for status updates every 2 seconds
            const interval = setInterval(() => {
                pollAnnouncementStatus(announcement_id);
            }, 2000);
            
            setPollingInterval(interval);
            
            // Do initial poll immediately
            pollAnnouncementStatus(announcement_id);

        } catch (err) {
            console.error('❌ Error creating announcement:', err);
            
            setLoading(false);
            
            // Provide specific error messages
            if (err.response) {
                const errorMessage = err.response.data?.detail || err.response.data?.message || 'Failed to create announcement';
                showError(`Server error: ${errorMessage}`);
            } else if (err.request) {
                showError('No response from server. Please check your internet connection and try again.');
            } else {
                showError(getActionErrorMessage(err, 'send announcement'));
            }
        }
    };





    return (
        <div className="universal-white-container make-announcement">
            <div className="modern-schedule-header make-announcement-header">
                <div className="announcement-header-content">
                    <div className="announcement-header-icon">
                        <FaBullhorn />
                    </div>
                    <div className="announcement-header-text">
                        <h1>Make Announcement</h1>
                        <p>Create and send targeted announcements to users across the platform</p>
                    </div>
                </div>
            </div>

            <div className="announcement-layout">
                <div className="main-content">
                    <form onSubmit={handleSubmit} className="announcement-form">
                        {/* Announcement Content */}
                        <div className="form-section">
                            <div className="section-header">
                                <div className="header-content">
                                    <div className="header-icon">
                                        <FaBullhorn />
                                    </div>
                                    <div className="header-text">
                                        <h3>Announcement Content</h3>
                                        <p>Create your announcement message and set its priority</p>
                                    </div>
                                </div>
                            </div>

                            <div className="section-content">
                                <div className="form-group">
                                    <label className="form-label">
                                        Announcement Title *
                                    </label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={announcement.title}
                                        onChange={(e) => handleInputChange('title', e.target.value)}
                                        placeholder="Enter a compelling announcement title..."
                                        required
                                    />
                                </div>

                                <div className="form-group">
                                    <label className="form-label">
                                        Message Content *
                                    </label>
                                    <textarea
                                        className="form-textarea"
                                        value={announcement.message}
                                        onChange={(e) => handleInputChange('message', e.target.value)}
                                        placeholder="Write your detailed announcement message here..."
                                        rows={6}
                                        required
                                    />
                                    <div className="character-count">
                                        {announcement.message.length} characters
                                    </div>
                                </div>

                                <div className="form-row">
                                    <div className="form-group">
                                        <label className="form-label">
                                            <span className="label-icon-wrapper">
                                                {getPriorityIcon(announcement.priority)}
                                            </span>
                                            Priority Level
                                        </label>
                                        <select
                                            className="form-select"
                                            value={announcement.priority}
                                            onChange={(e) => handleInputChange('priority', e.target.value)}
                                        >
                                            <option value="low">🟢 Low Priority</option>
                                            <option value="medium">🟡 Medium Priority</option>
                                            <option value="high">🔴 High Priority</option>
                                        </select>
                                    </div>

                                    <div className="form-group">
                                        <label className="form-label">
                                            <span className="label-icon-wrapper">
                                                <FaClock className="label-icon" />
                                            </span>
                                            Expiration Date (Optional)
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className="form-input"
                                            value={announcement.expires_at}
                                            onChange={(e) => handleInputChange('expires_at', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Target Audience */}
                        <div className="form-section">
                            <div className="section-header">
                                <div className="header-content">
                                    <div className="header-icon">
                                        <FaUsers />
                                    </div>
                                    <div className="header-text">
                                        <h3>Target Audience</h3>
                                        <p>Choose who will receive this announcement</p>
                                    </div>
                                </div>
                            </div>

                            <div className="section-content">
                                <div className="target-type-selector">
                                    <div 
                                        className={`target-option ${announcement.target_type === 'filters' ? 'active' : ''}`}
                                        onClick={() => handleInputChange('target_type', 'filters')}
                                    >
                                        <div className="option-content">
                                            <FaFilter className="option-icon" />
                                            <div className="option-text">
                                                <h4>Filter-based Targeting</h4>
                                                <p>Target users based on roles, curriculums, and other criteria</p>
                                            </div>
                                        </div>
                                        <input
                                            type="radio"
                                            name="target_type"
                                            value="filters"
                                            checked={announcement.target_type === 'filters'}
                                            onChange={(e) => handleInputChange('target_type', e.target.value)}
                                        />
                                    </div>
                                    
                                    <div 
                                        className={`target-option ${announcement.target_type === 'explicit' ? 'active' : ''}`}
                                        onClick={() => handleInputChange('target_type', 'explicit')}
                                    >
                                        <div className="option-content">
                                            <FaUserFriends className="option-icon" />
                                            <div className="option-text">
                                                <h4>Specific Users</h4>
                                                <p>Target specific users by their IDs</p>
                                            </div>
                                        </div>
                                        <input
                                            type="radio"
                                            name="target_type"
                                            value="explicit"
                                            checked={announcement.target_type === 'explicit'}
                                            onChange={(e) => handleInputChange('target_type', e.target.value)}
                                        />
                                    </div>
                                </div>

                                {announcement.target_type === 'filters' && (
                                    <div className="filters-container">
                                        <div className="filters-grid">
                                            {/* Account Types - Dropdown */}
                                            <div className="filter-card">
                                                <div className="filter-header">
                                                    <FaUsers className="filter-icon" />
                                                    <h4>Account Types</h4>
                                                </div>
                                                <div className="form-group">
                                                    <select
                                                        className="form-select"
                                                        value=""
                                                        onChange={(e) => {
                                                            if (e.target.value) {
                                                                handleFilterChange('account_types', e.target.value);
                                                            }
                                                        }}
                                                    >
                                                        <option value="">Select account types...</option>
                                                        {filterOptions.account_types.map(type => (
                                                            <option key={type.id} value={type.id}>
                                                                {type.name}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {announcement.target_filters.account_types.length > 0 && (
                                                        <div className="selected-tags">
                                                            {announcement.target_filters.account_types.map(id => {
                                                                const type = filterOptions.account_types.find(t => t.id === id);
                                                                return type ? (
                                                                    <span key={id} className="tag">
                                                                        {type.name}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => handleFilterChange('account_types', id)}
                                                                            className="tag-remove"
                                                                        >
                                                                            ×
                                                                        </button>
                                                                    </span>
                                                                ) : null;
                                                            })}
                                                        </div>
                                                    )}
                                                </div>
                                            </div>

                                            {/* Curriculums - Dropdown */}
                                            <div className="filter-card">
                                                    <div className="filter-header">
                                                        <FaGraduationCap className="filter-icon" />
                                                        <h4>Curriculums</h4>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-select"
                                                            value=""
                                                            disabled={filterOptions.curriculums.length === 0}
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    handleFilterChange('curriculums', e.target.value);
                                                                }
                                                            }}
                                                        >
                                                            <option value="">
                                                                {filterOptions.curriculums.length === 0 ? 'No curriculums available' : 'Select curriculums...'}
                                                            </option>
                                                            {filterOptions.curriculums.map(curriculum => (
                                                                <option key={curriculum.id} value={curriculum.id}>
                                                                    {curriculum.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {filterOptions.curriculums.length === 0 && (
                                                            <small className="filter-empty-hint">No curriculum options found yet.</small>
                                                        )}
                                                        {announcement.target_filters.curriculums.length > 0 && (
                                                            <div className="selected-tags">
                                                                {announcement.target_filters.curriculums.map(id => {
                                                                    const curriculum = filterOptions.curriculums.find(c => c.id === id);
                                                                    return curriculum ? (
                                                                        <span key={id} className="tag">
                                                                            {curriculum.name}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleFilterChange('curriculums', id)}
                                                                                className="tag-remove"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                            </div>

                                            {/* Ranks - Dropdown */}
                                            <div className="filter-card">
                                                    <div className="filter-header">
                                                        <FaStar className="filter-icon" />
                                                        <h4>Ranks</h4>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-select"
                                                            value=""
                                                            disabled={filterOptions.ranks.length === 0}
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    handleFilterChange('ranks', e.target.value);
                                                                }
                                                            }}
                                                        >
                                                            <option value="">
                                                                {filterOptions.ranks.length === 0 ? 'No ranks available' : 'Select ranks...'}
                                                            </option>
                                                            {filterOptions.ranks.map(rank => (
                                                                <option key={rank.id} value={rank.id}>
                                                                    {rank.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {filterOptions.ranks.length === 0 && (
                                                            <small className="filter-empty-hint">No rank options found yet.</small>
                                                        )}
                                                        {announcement.target_filters.ranks.length > 0 && (
                                                            <div className="selected-tags">
                                                                {announcement.target_filters.ranks.map(id => {
                                                                    const rank = filterOptions.ranks.find(r => r.id === id);
                                                                    return rank ? (
                                                                        <span key={id} className="tag">
                                                                            {rank.name}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleFilterChange('ranks', id)}
                                                                                className="tag-remove"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                            </div>

                                            {/* Rotations/Experiences (renamed from Specialities) - Dropdown */}
                                            <div className="filter-card">
                                                    <div className="filter-header">
                                                        <FaHospital className="filter-icon" />
                                                        <h4>Rotations/Experiences</h4>
                                                        <span className="filter-subtitle">(Preceptors only)</span>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-select"
                                                            value=""
                                                            disabled={filterOptions.specialities.length === 0}
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    handleFilterChange('specialities', parseInt(e.target.value));
                                                                }
                                                            }}
                                                        >
                                                            <option value="">
                                                                {filterOptions.specialities.length === 0 ? 'No rotations/experiences available' : 'Select rotations/experiences...'}
                                                            </option>
                                                            {filterOptions.specialities.map(speciality => (
                                                                <option key={speciality.id} value={speciality.id}>
                                                                    {speciality.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {filterOptions.specialities.length === 0 && (
                                                            <small className="filter-empty-hint">No rotation/experience options found yet.</small>
                                                        )}
                                                        {announcement.target_filters.specialities.length > 0 && (
                                                            <div className="selected-tags">
                                                                {announcement.target_filters.specialities.map(id => {
                                                                    const speciality = filterOptions.specialities.find(s => s.id === id);
                                                                    return speciality ? (
                                                                        <span key={id} className="tag">
                                                                            {speciality.name}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleFilterChange('specialities', id)}
                                                                                className="tag-remove"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                            </div>

                                            {/* Institutions - Dropdown */}
                                            <div className="filter-card">
                                                    <div className="filter-header">
                                                        <FaBuilding className="filter-icon" />
                                                        <h4>Institutions</h4>
                                                        <span className="filter-subtitle">(Preceptors only)</span>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-select"
                                                            value=""
                                                            disabled={filterOptions.institutions.length === 0}
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    handleFilterChange('institutions', parseInt(e.target.value));
                                                                }
                                                            }}
                                                        >
                                                            <option value="">
                                                                {filterOptions.institutions.length === 0 ? 'No institutions available' : 'Select institutions...'}
                                                            </option>
                                                            {filterOptions.institutions.map(institution => (
                                                                <option key={institution.id} value={institution.id}>
                                                                    {institution.name}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {filterOptions.institutions.length === 0 && (
                                                            <small className="filter-empty-hint">No institution options found yet.</small>
                                                        )}
                                                        {announcement.target_filters.institutions.length > 0 && (
                                                            <div className="selected-tags">
                                                                {announcement.target_filters.institutions.map(id => {
                                                                    const institution = filterOptions.institutions.find(i => i.id === id);
                                                                    return institution ? (
                                                                        <span key={id} className="tag">
                                                                            {institution.name}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => handleFilterChange('institutions', id)}
                                                                                className="tag-remove"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        )}
                                                    </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {announcement.target_type === 'explicit' && (
                                    <div className="explicit-users-container">
                                        {loadingUsers ? (
                                            <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280' }}>
                                                <LoadingSpinner text="Loading users..." variant="inline" />
                                            </div>
                                        ) : (
                                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem' }}>
                                                {/* Students Multi-Select Dropdown */}
                                                <div className="filter-card">
                                                    <div className="filter-header">
                                                        <FaGraduationCap className="filter-icon" />
                                                        <h4>Students</h4>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-select"
                                                            value=""
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    const userId = e.target.value;
                                                                    if (!announcement.target_user_ids.includes(userId)) {
                                                                        setAnnouncement(prev => ({
                                                                            ...prev,
                                                                            target_user_ids: [...prev.target_user_ids, userId]
                                                                        }));
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Select students...</option>
                                                            {students.map(student => (
                                                                <option key={student._id} value={student._id}>
                                                                    {student.firstname} {student.lastname}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {announcement.target_user_ids.filter(id => students.some(s => s._id === id)).length > 0 && (
                                                            <div className="selected-tags">
                                                                {announcement.target_user_ids.filter(id => students.some(s => s._id === id)).map(id => {
                                                                    const student = students.find(s => s._id === id);
                                                                    return student ? (
                                                                        <span key={id} className="tag">
                                                                            {student.firstname} {student.lastname}
                                                                            {student.rank && (
                                                                                <span style={{
                                                                                    background: '#3b82f6',
                                                                                    color: 'white',
                                                                                    padding: '0.125rem 0.375rem',
                                                                                    borderRadius: '8px',
                                                                                    fontSize: '0.7rem',
                                                                                    marginLeft: '0.375rem',
                                                                                    fontWeight: 600
                                                                                }}>
                                                                                    {student.rank}
                                                                                </span>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setAnnouncement(prev => ({
                                                                                        ...prev,
                                                                                        target_user_ids: prev.target_user_ids.filter(uid => uid !== id)
                                                                                    }));
                                                                                }}
                                                                                className="tag-remove"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className="user-count">
                                                            {announcement.target_user_ids.filter(id => students.some(s => s._id === id)).length} students selected
                                                        </div>
                                                    </div>
                                                </div>

                                                {/* Preceptors Multi-Select Dropdown */}
                                                <div className="filter-card">
                                                    <div className="filter-header">
                                                        <FaUsers className="filter-icon" />
                                                        <h4>Preceptors</h4>
                                                    </div>
                                                    <div className="form-group">
                                                        <select
                                                            className="form-select"
                                                            value=""
                                                            onChange={(e) => {
                                                                if (e.target.value) {
                                                                    const userId = e.target.value;
                                                                    if (!announcement.target_user_ids.includes(userId)) {
                                                                        setAnnouncement(prev => ({
                                                                            ...prev,
                                                                            target_user_ids: [...prev.target_user_ids, userId]
                                                                        }));
                                                                    }
                                                                }
                                                            }}
                                                        >
                                                            <option value="">Select preceptors...</option>
                                                            {preceptors.map(preceptor => (
                                                                <option key={preceptor._id} value={preceptor._id}>
                                                                    {preceptor.firstname} {preceptor.lastname}
                                                                </option>
                                                            ))}
                                                        </select>
                                                        {announcement.target_user_ids.filter(id => preceptors.some(p => p._id === id)).length > 0 && (
                                                            <div className="selected-tags">
                                                                {announcement.target_user_ids.filter(id => preceptors.some(p => p._id === id)).map(id => {
                                                                    const preceptor = preceptors.find(p => p._id === id);
                                                                    return preceptor ? (
                                                                        <span key={id} className="tag">
                                                                            {preceptor.firstname} {preceptor.lastname}
                                                                            {preceptor.speciality && (
                                                                                <span style={{
                                                                                    background: '#10b981',
                                                                                    color: 'white',
                                                                                    padding: '0.125rem 0.375rem',
                                                                                    borderRadius: '8px',
                                                                                    fontSize: '0.7rem',
                                                                                    marginLeft: '0.375rem',
                                                                                    fontWeight: 600
                                                                                }}>
                                                                                    {preceptor.speciality}
                                                                                </span>
                                                                            )}
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => {
                                                                                    setAnnouncement(prev => ({
                                                                                        ...prev,
                                                                                        target_user_ids: prev.target_user_ids.filter(uid => uid !== id)
                                                                                    }));
                                                                                }}
                                                                                className="tag-remove"
                                                                            >
                                                                                ×
                                                                            </button>
                                                                        </span>
                                                                    ) : null;
                                                                })}
                                                            </div>
                                                        )}
                                                        <div className="user-count">
                                                            {announcement.target_user_ids.filter(id => preceptors.some(p => p._id === id)).length} preceptors selected
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="form-actions">
                            {loading && announcementStatus && (
                                <div style={{
                                    flex: 1,
                                    background: 'linear-gradient(135deg, #e0f2fe 0%, #dbeafe 100%)',
                                    border: '2px solid #3b82f6',
                                    borderRadius: '12px',
                                    padding: '1.25rem 1.5rem',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1rem',
                                    boxShadow: '0 4px 6px -1px rgba(59, 130, 246, 0.1)',
                                }}>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        gap: '1rem',
                                    }}>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.75rem',
                                        }}>
                                            <LoadingSpinner showText={false} variant="inline" size="small" fullWidth={false} />
                                            <div>
                                                <div style={{
                                                    fontSize: '1rem',
                                                    color: '#1e40af',
                                                    fontWeight: 600,
                                                    marginBottom: '0.25rem'
                                                }}>
                                                    Sending Notifications...
                                                </div>
                                                <div style={{
                                                    fontSize: '0.875rem',
                                                    color: '#3b82f6',
                                                    fontWeight: 500
                                                }}>
                                                    {announcementStatus.notifications_sent || 0} / {announcementStatus.target_count || 0} sent
                                                    {announcementStatus.notifications_failed > 0 && (
                                                        <span style={{ color: '#ef4444', marginLeft: '0.5rem' }}>
                                                            ({announcementStatus.notifications_failed} failed)
                                                        </span>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                        <div style={{
                                            fontSize: '1.5rem',
                                            fontWeight: 700,
                                            color: '#1e40af',
                                        }}>
                                            {announcementStatus.target_count > 0 
                                                ? Math.round((announcementStatus.notifications_sent / announcementStatus.target_count) * 100)
                                                : 0}%
                                        </div>
                                    </div>
                                    {/* Progress Bar */}
                                    <div style={{
                                        width: '100%',
                                        height: '8px',
                                        background: '#e0f2fe',
                                        borderRadius: '4px',
                                        overflow: 'hidden',
                                        border: '1px solid #bfdbfe'
                                    }}>
                                        <div style={{
                                            height: '100%',
                                            background: 'linear-gradient(90deg, #3b82f6 0%, #2563eb 100%)',
                                            width: `${announcementStatus.target_count > 0 
                                                ? (announcementStatus.notifications_sent / announcementStatus.target_count) * 100
                                                : 0}%`,
                                            transition: 'width 0.3s ease-in-out',
                                            borderRadius: '4px'
                                        }}></div>
                                    </div>
                                </div>
                            )}
                            {!loading && (
                                <>
                                    <button 
                                        type="button"
                                        className="preview-btn"
                                        onClick={() => setPreviewMode(!previewMode)}
                                    >
                                        <FaEye />
                                        {previewMode ? 'Hide Preview' : 'Preview Announcement'}
                                    </button>
                                    
                                    <button 
                                        type="submit" 
                                        className="send-btn"
                                        disabled={loading || !announcement.title || !announcement.message}
                                    >
                                        <FaPaperPlane />
                                        Send Announcement
                                    </button>
                                </>
                            )}
                        </div>
                    </form>
                </div>

                {/* Sidebar - Preview or History */}
                <div className="sidebar">
                    {previewMode && (
                        <div className="preview-panel">
                            <div className="preview-header">
                                <h3>
                                    <FaEye />
                                    Announcement Preview
                                </h3>
                            </div>
                            <div className="preview-content">
                                <div className="preview-announcement">
                                    <div className="announcement-header">
                                        <div className="announcement-priority">
                                            {getPriorityIcon(announcement.priority)}
                                            <span>{getPriorityLabel(announcement.priority)}</span>
                                        </div>
                                        <div className="announcement-date">
                                            {new Date().toLocaleDateString()}
                                        </div>
                                    </div>
                                    <h4 className="announcement-title">
                                        {announcement.title || 'Announcement Title'}
                                    </h4>
                                    <p className="announcement-message">
                                        {announcement.message || 'Your announcement message will appear here...'}
                                    </p>
                                    {announcement.expires_at && (
                                        <div className="announcement-expiry">
                                            <FaClock />
                                            Expires: {new Date(announcement.expires_at).toLocaleString()}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}


                </div>
            </div>
        </div>
    );
};

export default MakeAnnouncement;
